import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { BarsMenu, LakeIcon, LogoWithText, MyProfileIcon, StatusesIcon } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { loginActions, userAuthQueries } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { allBreadcrumbs } from 'resources/constants/allBreadcrumbs';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';

import Element from './Element';
import Title from './Title';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './NavigationMobile.scss';

const NavigationMobile = () => {
  const dispatch = useDispatch(),
        { pathname } = useLocation(),
        navigate = useNavigate(),
        timeout = useRef(),
        iconRef = useRef(),
        [isAnimation, setIsAnimation] = useState(false),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        changeMenu = useCallback(() => {
          setIsAnimation(true)
          timeout.current = setTimeout(() => setIsAnimation(false), 201)
          setIsMenuOpen(prevIsMenuOpen => !prevIsMenuOpen)
          if(isMenuOpen){
            iconRef.current.closeAnimationElement.beginElement()
          } else {
            iconRef.current.openAnimationElement.beginElement()
          }
        }, [isMenuOpen]),
        closeMenu = useCallback(() => {
          iconRef.current.closeAnimationElement.beginElement()
          setIsMenuOpen(false)
        },[]),
        [defaultImg, setDefaultImg] = useState(false),
        [currentDirectory, setCurrentDirectory] = useState(() => Object.values(allBreadcrumbs).find(directory => pathname.startsWith(directory.path))),
        isDev = process.env.NODE_ENV !== 'production',
        isLocalHost = properties.version === "LOCALHOST",
        onMenuItemClick = useCallback(path => {
          closeMenu()
          navigate(path)
        },[closeMenu, navigate]),
        getQueryBuilderNoError = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: logOutCall } = userAuthQueries.useLogOutMutation({ getQueryBuilder: getQueryBuilderNoError }),
        logOut = useCallback(() => dispatch(loginActions.logOut({ logOutCall })), [dispatch, logOutCall]),
        getTitle = useCallback(() => setCurrentDirectory(Object.values(allBreadcrumbs).find(directory => pathname.startsWith(directory.path))), [pathname]),
        enviromentIndicator = useMemo(() => {
          let currentEnviroment = ""
          if(isDev && !isLocalHost){
            currentEnviroment = "DEV"
          } else if (isLocalHost){
            currentEnviroment = "Local"
          }
          return (
            <div className="navigation-mobile__navbar__logo__dev">{currentEnviroment}</div>
          )
        }, [isDev, isLocalHost]),
        hasAccessTo = useCallback(component => dispatch(loginActions.hasAccessTo(component)), [dispatch])

  useEffect(() => {
    getTitle()
  },[getTitle])

  return (
    <div className="navigation-mobile">
      <div className="navigation-mobile__navbar">
        <div 
            className="navigation-mobile__navbar__toggle" 
            onClick={changeMenu}
        >
          <BarsMenu
              color="white-base"
              height={36}
              ref={iconRef}
              width={36}
          />
        </div>
        <Title
            icon={currentDirectory?.icon}
            title={currentDirectory?.name}
        />
        <div className="navigation-mobile__navbar__logo">
          <div className="navigation-mobile__navbar__logo__bg"/>
          <div className="navigation-mobile__navbar__logo__content">
            <If condition={properties.logoUrl !== "" && !defaultImg}>
              <Then>
                {() => (
                  <img 
                      onError={() => setDefaultImg(true)}
                      src={properties.logoUrl}
                  />
                )}
              </Then>
              <Else>
                {() => (
                  <div className="image-component">
                    <LogoWithText/>
                  </div>
                )}
              </Else>
            </If>
          </div>
          {enviromentIndicator}
        </div>
      </div>
      <div
          className={classNames(
            "navigation-mobile__sidebar",
            { "navigation-mobile__sidebar--is-open": isMenuOpen }
          )}
      >
        <When condition={isMenuOpen}>
          {() => (
            <div
                className="navigation-mobile__sidebar__background"
                onClick={closeMenu}
            />
          )}
        </When>
        <div className="navigation-mobile__sidebar__group">
          <When condition={hasAccessTo(routing.statuses.name)}>
            {() => (
              <Element
                  icon={<StatusesIcon/>}
                  isAnimation={isAnimation}
                  onClick={onMenuItemClick}
                  path={DashboardLinkFactory.statuses.root({ baseUrl: "" })}
                  selected={String(pathname).startsWith(`/${routing.statuses.name}`)}
                  text={routing.statuses.readableName}
              />
            )}
          </When>
          <When condition={hasAccessTo(routing.dataLake.name)}>
            {() => (
              <Element
                  icon={<LakeIcon/>}
                  isAnimation={isAnimation}
                  onClick={onMenuItemClick}
                  path={DashboardLinkFactory.dataLake.list({ baseUrl: "", query: "", tab: routing.dataLake.routing.groups.name })}
                  selected={String(pathname).startsWith(`/${routing.dataLake.name}`)}
                  text={routing.dataLake.readableName}
              />
            )}
          </When>
        </div>
        <div className="navigation-mobile__sidebar__group--is-on-bottom">
          <Element
              icon={<MyProfileIcon/>}
              isAnimation={isAnimation}
              onClick={logOut}
              text={routing.logout.readableName}
          />
        </div>
      </div>
    </div>
  )
}

export default NavigationMobile;
